import { React, useState, useMemo, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Search, CaretLeft, CaretRight, Container, Flex, Skeleton, MTabs, MLink, Heading, Text, MTag } from "@cisco/spaces-dash-lib-styleguide";
import { LocationTree } from "@cisco/spaces-dash-app-lh2";
import { useSelector } from "react-redux";
import { SelectLicense } from "@cisco/spaces-dash-lib-header";
import mapDisabled from "../../assets/images/hierarchy-base/3d-2d-disabled.svg";
import _2D from "../../assets/images/hierarchy-base/3d-2d-on.svg";
import _3D from "../../assets/images/hierarchy-base/3d-on-2d.svg";
import rmsInfo from "../../assets/images/hierarchy-base/rms-info.svg";
import moveMouse from "../../assets/images/hierarchy-base/move_mouse_control.svg";
import zoomTouch from "../../assets/images/hierarchy-base/zoom_touch_control.svg";
import moveMouseAlt from "../../assets/images/hierarchy-base/move_mouse_control2.svg";
import zoomTouchAlt from "../../assets/images/hierarchy-base/zoom_touch_control2.svg";
import mapLoader from "../../assets/images/hierarchy-base/map-loader.gif";
import BusinessLocationDetails from "../business-location-details/business-location-details";
import NetworkDevicesBase from "../network-devices-base/network-devices-base";
import LicenseUsage from "../license-usage/license-usage";
import * as BusinessHierarchyService from "../../service/business-hierarchy-service";
import { I18nContext } from "../../contexts/i18nContext";
import LocationMetadata from "../location-metadata/location-metadata";
import "../../common/common_style.scss";
import "../../common/global_style.scss";
import "./hierarchy-base.scss";
import LocationInfo from "../location-info/location-info";
import HierarchyNodes from "../hierarchy-nodes/hierarchy-nodes";
import useLoader from "../../hooks/useLoader";
import LocationMap from "../location-map/location-map";
import RmsBase from "../rms-base/rms-base";
import FloorMap from "../floor-map/floor-map";
import ProximityRules from "../proximity-rules/proximity-rules";
import UserTable from "../users/users";
import { PropertiesContext } from "../../contexts/propertiesContext";

const MemoizedUserTable = ({ searchBy }) => {
  return useMemo(() => <UserTable searchBy={searchBy} />, [searchBy]);
};

function HierarchyBase() {
  const i18n = useContext(I18nContext);
  const { DASHBOARD_CLIENT_GOOGLE_API_KEY: googleMapApiKey, DASHBOARD_CLIENT_MAPBOXGL_API_KEY: mapboxApiKey } = useContext(PropertiesContext) || {};
  const [locCollapsed, setLocCollapsed] = useState(false);
  const [searchActive] = useState(false);
  const [locationObjLoaded, setLocationObjLoaded] = useState(false);
  const [showLocationByType, setShowLocationByType] = useState(false);
  const [changeLicensePopup] = useState(false);
  const [isSmartLicenseRegistered, setIsSmartLicenseRegistered] = useState(false);
  const [wlcLocationData, setWlcLocationData] = useState({});
  const [selectedNodeState, setSelectedNodeState] = useState({});
  const [selectedNodeDetails, setSelectedNodeDetails] = useState({});
  const [locationRowMap, setLocationRowMap] = useState({});
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const { setLoading } = useLoader();
  const [isLicenseFetched, setIsLicenseFetched] = useState(false);
  const [licenseType, setLicenseType] = useState("");
  const [licenseColor, setLicenseColor] = useState("#6977F0");
  const licenseLevel = useRef("");
  const customerLicenseType = useRef("");
  const isSmartLicenseEnabled = useRef(false);
  const [selectedTab, setSelectedTab] = useState("map");
  const [richMap] = useState(true);
  const [show3DMap, setShow3DMap] = useState(false);
  const [displayHint, setDisplayHint] = useState(false);
  const [locationId, setLocationId] = useState("");
  const [locationName, setLocationName] = useState("");
  const [richMapCheckingLoader] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [zoneDrawerOpen, setZoneDrawerOpen] = useState(false);

  const [availableLicenses, setAvailableLicenses] = useState([]);
  const serverAvailableLicenses = useRef([]);

  const [geoLocationData, setGeoLocationData] = useState({
    isFirstTime: false,
    coordinatesData: []
  });
  const [rmsDependenciesLoaded, setRmsDependenciesLoaded] = useState(false);

  const userName = sessionStorage.getItem("userName") ? sessionStorage.getItem("userName") : sessionStorage.getItem("supportUserName");
  const userTenantGeo = `${sessionStorage.getItem("tenantId")}${userName}-geolocation`;

  const [showRms, setShowRms] = useState(false);
  const [isFloorLocation, setIsFloorLocation] = useState(false);
  const [rmsFloor, setRmsFloor] = useState({});
  const [rmsBuilding, setRmsBuilding] = useState({});

  const locationState = useSelector((state) => state.location);
  let selectedNode;
  // let floorLocation;
  const locationMap = useSelector((state) => state.locationRowMap);

  const [deltaUpdateTime, setDeltaUpdateTime] = useState(new Date());
  const locationUpdate = useSelector((state) => state.locationUpdate);
  const [refreshMap, setRefreshMap] = useState(true);
  const userDetails = useSelector((state) => state.userDetails);
  const [licensesLoaded, setLicensesLoaded] = useState(false);

  const treeParams = {
    type: "single",
    enableSearch: true,
    isPopup: false,
    isReportsLocation: false,
    isDataId: false,
    appData: {},
    restrictedLocations: [],
    appName: "business-hierarchy"
  };

  const [searchBy, setSearchBy] = useState("");
  const { pathname } = useLocation();

  const searchByAdminRoles = (e, type) => {
    setSearchBy(e.target.value, type);
  };

  const onClearSearch = () => {
    setSearchBy("");
  };

  const toggleCollapseLoc = () => {
    setLocCollapsed((locCollapsedPrev) => !locCollapsedPrev);
  };

  const selectTab = (tab) => {
    setSelectedTab(tab);
  };

  const selectLocationTab = () => {
    setSelectedTab("locInfo");
  };

  const updateLicenseType = (lType) => {
    const licenses = serverAvailableLicenses.current;
    const typeMatch = licenses.find((license) => license.type === lType);

    if (typeMatch) {
      const { displayName, color } = typeMatch;
      setLicenseType(displayName);
      setLicenseColor(color?.solid);
      licenseLevel.current = displayName;
      customerLicenseType.current = displayName;
    } else {
      const variationMatch = licenses.find((license) => license.variations.includes(lType));

      if (variationMatch) {
        console.log("Variation Match:", variationMatch);
        const displayName2 = variationMatch.displayName;
        const color2 = variationMatch.color;
        setLicenseType(displayName2);
        licenseLevel.current = displayName2;
        customerLicenseType.current = displayName2;
        setLicenseColor(color2?.solid);
      } else {
        setLicenseType(lType);
        licenseLevel.current = lType;
        customerLicenseType.current = lType;
      }
    }
  };

  const getUncategorisedApCounts = () => {
    setWlcLocationData({});
    const allLocationMap = locationRowMap && locationRowMap.length > 1 ? locationRowMap : []; // this.dataService.getLocationRowMap();
    const wlcLocations = [];
    allLocationMap.forEach((e) => {
      if (e.type && e.type[0] == "WLC") {
        wlcLocations.push(e.id);
      }
    });
    // setLoading(true);
    BusinessHierarchyService.unCategorisedApCount({
      locationIds: wlcLocations
    })
      .then((data) => {
        setLoading(false);
        setWlcLocationData({ ...data.data });
      })
      .catch(() => {
        setLoading(false);
        console.log("BusinessHierarchyService: Error on unCategorisedApCount");
      });
  };

  // const getRecentSearchItems = () => {
  //   // setLoading(true);
  //   BusinessHierarchyService.recentSearch()
  //     .then((data) => {
  //       if (data.status && data.data) {
  //         setRecentSearch([]);
  //         Object.keys(data.data).forEach((key) => {
  //           const locArray = data.data[key];
  //           if (locArray && locArray.length) {
  //             locArray.forEach((locId) => {
  //               const locObj = locationRowMap[locId];
  //               if (locObj) {
  //                 setRecentSearch([
  //                   ...recentSearch,
  //                   {
  //                     id: locObj.id,
  //                     name: locObj.name,
  //                     path: locObj.locationPath
  //                   }
  //                 ]);
  //               }
  //             });
  //           }
  //         });
  //       } else {
  //         setRecentSearch([]);
  //       }
  //     })
  //     .catch(() => {
  //       setRecentSearch([]);
  //       setLoading(false);
  //       console.log("BusinessHierarchyService: Error on recentSearch");
  //     });
  // };

  const selectedNodeLocationCounts = () => {
    setSelectedNodeDetails(null);
    const locationArray = {
      locationIds: []
    };
    if (locationState && locationState[0]) {
      locationArray.locationIds.push(locationState[0].id);
      // setLoading(true);
      BusinessHierarchyService.getMapDetails(locationArray)
        .then((mapDetails) => {
          setLoading(false);
          if (mapDetails && mapDetails.data && mapDetails.data.length) {
            setSelectedNodeDetails({
              ...mapDetails.data[0],
              data: { access: locationState[0]?.access }
            });
          } else {
            setSelectedNodeDetails(null);
          }
        })
        .catch(() => {
          setLoading(false);
          console.log("BusinessHierarchyService: Error on selectedNodeLocationCounts");
        });
    }
  };

  const getFilteredGeoLocations = (geoLocations) => {
    let coordData = null;
    // filter campuses from geolocations
    const allCampuses = geoLocations.filter((e) => {
      return (
        e.inferredLocationTypes.indexOf("CAMPUS") > -1 ||
        e.inferredLocationTypes.indexOf("NETWORK") > -1 ||
        e.inferredLocationTypes.indexOf("ORG") > -1 ||
        e.inferredLocationTypes.indexOf("CMX") > -1 ||
        e.inferredLocationTypes.indexOf("WLC") > -1
      );
    });

    if (allCampuses && allCampuses.length) {
      coordData = allCampuses;
    } else {
      const allBuildings = geoLocations.filter((e) => {
        return e.inferredLocationTypes.indexOf("BUILDING") > -1;
      });
      if (allBuildings && allBuildings.length) {
        coordData = allBuildings;
      } else {
        const allFloors = geoLocations.filter((e) => {
          return e.inferredLocationTypes.indexOf("FLOOR") > -1;
        });
        if (allFloors && allFloors.length) {
          coordData = allFloors;
        } else {
          coordData = null;
        }
      }
    }
    return coordData;
  };

  const getChildrenMapLocations = () => {
    let coordData = null;
    if (selectedNode && selectedNode.data) {
      const selectedNodeId = selectedNode.data.id;
      const geoLocations = []; // TODO: this.businessHierarchyService.getGeoLocationRowArray();
      if (selectedNode && selectedNode.data) {
        if (selectedNode.data.type && selectedNode.data.type[0] == "ROOT") {
          coordData = getFilteredGeoLocations(geoLocations);
        } else if (selectedNode.data.type && selectedNode.data.type[0]) {
          // filter campuses from geolocations
          const allChildren = geoLocations.filter((e) => {
            return e.locationPath.indexOf(selectedNodeId) > -1;
          });
          coordData = getFilteredGeoLocations(allChildren);
        }
      }
    }
    return coordData;
  };

  const prepareLocationMapDetails = () => {
    selectedNodeLocationCounts();
    getChildrenMapLocations();
  };

  const canShow3DSwitch = () => {
    if (selectedNodeState && selectedNodeState.data && selectedNodeState.data.type && selectedNodeState.data.type[0] == "FLOOR") {
      return true;
    }
    return false;
  };

  const toggle3DView = (action) => {
    setShow3DMap(action);
    setShowRms(action);
  };

  const toggleRMSHint = () => {
    setDisplayHint((displayHintPrev) => !displayHintPrev);
  };

  const hideHint = () => {
    setDisplayHint(false);
  };

  function ContainerLoader() {
    return (
      <Flex direction="vertical" gap={24}>
        <Container
          style={{
            height: 194,
            width: "100%",
            paddingLeft: 27,
            borderBottom: "1px solid #d9d9d9",
            boxShadow: "none",
            borderRadius: 0
          }}
        >
          <Flex gap={24}>
            <Skeleton
              height={50}
              style={{
                width: 50
              }}
            />
            <Flex direction="vertical" gap={12} grow>
              <Skeleton
                height="md"
                style={{
                  width: "40%"
                }}
              />
              <Skeleton
                height="sm"
                style={{
                  width: "15%",
                  marginLeft: "-10px"
                }}
              />
            </Flex>
          </Flex>
          <Flex gap={50} style={{ paddingTop: "30px" }}>
            <Skeleton
              height={45}
              style={{
                width: 32
              }}
            />
            <Skeleton
              height={45}
              style={{
                width: 32
              }}
            />
            <Skeleton
              height={45}
              style={{
                width: 32
              }}
            />
            <Skeleton
              height={45}
              style={{
                width: 32
              }}
            />
            <Skeleton
              height={45}
              style={{
                width: 32
              }}
            />
            <Skeleton
              height={45}
              style={{
                width: 32
              }}
            />
          </Flex>
          <Flex gap={40}>
            <Skeleton
              height="12px"
              style={{
                width: "45px",
                marginTop: "5px",
                height: "12px"
              }}
            />
            <Skeleton
              height="12px"
              style={{
                width: "45px",
                marginTop: "5px",
                height: "12px"
              }}
            />
            <Skeleton
              height="12px"
              style={{
                width: "45px",
                marginTop: "5px",
                height: "12px"
              }}
            />
            <Skeleton
              height="12px"
              style={{
                width: "45px",
                marginTop: "5px",
                height: "12px"
              }}
            />
            <Skeleton
              height="12px"
              style={{
                width: "45px",
                marginTop: "5px",
                height: "12px"
              }}
            />
            <Skeleton
              height="12px"
              style={{
                width: "45px",
                marginTop: "5px",
                height: "12px"
              }}
            />
          </Flex>
        </Container>
      </Flex>
    );
  }

  const prepareGeoLocationData = (newGeoLocationData) => {
    console.log(`Geolocation data for ${userTenantGeo}`, newGeoLocationData);
    if (newGeoLocationData && newGeoLocationData.data) {
      setGeoLocationData({
        isFirstTime: false,
        coordinatesData: newGeoLocationData.data
      });
    }
  };

  const getIndexedDbGeolocationData = () => {
    try {
      const dbName = "dashboardDB";
      const dbVersion = localStorage.getItem("dbVersion") ? localStorage.getItem("dbVersion") : 1;
      const request = indexedDB.open(dbName, dbVersion);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        const storeName = "locationMap";
        db.transaction(storeName).objectStore(storeName).get(userTenantGeo).onsuccess = (tenantevent) => {
          console.log(`${userTenantGeo} is ${tenantevent.target.result}`);
        };
      };

      request.onsuccess = function (successevent) {
        const db = successevent.target.result;
        const transaction = db.transaction("locationMap", "readwrite");
        const objectStore = transaction.objectStore("locationMap");
        objectStore.get(userTenantGeo).onsuccess = (event) => {
          prepareGeoLocationData(event.target.result);
        };
      };
    } catch (err) {
      console.log("Error fetching geolocation data", err);
    }
  };

  const loadExternalScript = (url) => {
    const script = document.createElement("script");
    script.src = url;
    script.id = "rms-script";
    script.async = false;
    document.body.appendChild(script);
    script.addEventListener(
      "load",
      function () {
        setRmsDependenciesLoaded(true);
      },
      false
    );
    return () => {
      document.body.removeChild(script);
    };
  };

  // const loadExternalStyle = (url) => {
  //   const extLink = document.createElement("link");
  //   extLink.href = url;
  //   extLink.async = true;
  //   document.body.appendChild(extLink);

  //   return () => {
  //     document.body.removeChild(extLink);
  //   };
  // };

  const prepareRMSDependencies = () => {
    if (!document.getElementById("rms-script")) {
      // get setup and endpoint url for RMS domain
      let endpointUrl = "io";
      const searchParams = new URLSearchParams(window.location.search);
      console.log("Domain::", searchParams.get("domain"));
      const hostDomain = searchParams.get("domain") != null ? searchParams.get("domain") : window.location.host;
      const domainHost = hostDomain.split(".");
      const endPoint = domainHost[domainHost.length - 1];
      if (endPoint === "eu") {
        endpointUrl = "eu";
      } else if (endPoint === "sg") {
        endpointUrl = "sg";
      } else if (endPoint === "cisco") {
        endpointUrl = "cisco";
      }
      const hostSplit = window.location.host.split(".");
      let setup = "";
      if (hostSplit.length) {
        if (hostSplit.length > 0) {
          setup = hostSplit.length > 2 ? hostSplit[1] : hostSplit[0];
        }
        console.log("setup", setup, "endpoint_url", endpointUrl);

        // load rms dependencies
        // const mapBoxUrl =
        //             "https://rms." +
        //             setup +
        //             "." +
        //             endpoint_url +
        //             "/js/mapbox-gl.js";
        // const mapBoxCssUrl =
        //             "https://rms." +
        //             setup +
        //             "." +
        //             endpoint_url +
        //             "/css/mapbox-gl.css";
        // const threeMinJs = "https://unpkg.com/three@0.126.0/build/three.min.js";
        // const gltFLoaderJs = "https://unpkg.com/three@0.126.0/examples/js/loaders/GLTFLoader.js";
        // const richMapUrl = "https://rms." + setup + "." + endpointUrl + "/js/rich-maps-0.1.3.js";
        const richMapUrl = `https://rms.${setup}.${endpointUrl}/js/rich-maps-0.1.6.js`;
        loadExternalScript(richMapUrl);
        // loadExternalScript(mapBoxUrl);
        // loadExternalStyle(mapBoxCssUrl);
        // setTimeout(() => {
        //   loadExternalScript(threeMinJs);
        //   setTimeout(() => {
        //     loadExternalScript(gltFLoaderJs);
        //     setTimeout(() => {
        //       loadExternalScript(richMapUrl);
        //     }, 500);
        //   }, 500);
        // }, 500);
      }
    }
  };

  const activeNode = (node) => {
    setIsSmartLicenseRegistered(sessionStorage.getItem("isSmartLicenseRegistered") ? sessionStorage.getItem("isSmartLicenseRegistered") : false);
    setShowLocationByType(true);
    setLocationObjLoaded(false);
    setIsFloorLocation(false);
    setShowRms(false);
    setShow3DMap(false);

    setTimeout(() => {
      if (selectedNode && selectedNode.data && selectedNode.data.type && selectedNode.data.type[0] == "FLOOR") {
        setIsFloorLocation(true);
        // TODO: this.fetchRMS();
        const buildingLocation = locationMap[selectedNode.data.parentId];
        if (buildingLocation && buildingLocation.richMapInfo && buildingLocation.richMapInfo.richMapStatus && buildingLocation.richMapInfo.richMapStatus === "PUBLISHED") {
          const floorLoc = selectedNode.data;
          if (floorLoc && floorLoc.richMapInfo && floorLoc.richMapInfo.lhLevel !== undefined && floorLoc.richMapInfo.lhLevel !== null) {
            setShowRms(true);
            setShow3DMap(true);
            setRmsBuilding(buildingLocation);
            setRmsFloor(selectedNode.data);
          } else {
            setShowRms(false);
            setShow3DMap(false);
          }
        } else {
          setShowRms(false);
          setShow3DMap(false);
        }
      } else {
        setIsFloorLocation(false);
        setShowRms(false);
        setShow3DMap(false);
      }
    }, 100);

    // console.log("rootLocationId:" + sessionStorage.getItem("rootLocationId"));
    const rootLocation = sessionStorage.getItem("rootLocationId");
    if (rootLocation && node.node && node.node.data && node.node.data.id && rootLocation !== node.node.data.id) {
      // this.dataService.selectedLH2Location = node.node.data.id;
    }

    if (selectedNode && selectedNode.data) {
      // assign loc data
      const selectedNodeLocData = { ...selectedNode.data };
      let selectedNodeLocType = selectedNodeLocData?.type && selectedNodeLocData.type[0] ? selectedNodeLocData.type[0] : "";
      selectedNodeLocType = selectedNodeLocType.toLowerCase();
      if (selectedNodeLocType == "ap_zone" || selectedNodeLocType == "zone" || selectedNodeLocType == "cmxzone" || selectedNodeLocType == "polygon_zone") {
        selectedNodeLocType = "zone";
      }
      if (selectedNodeLocType == "zone") {
        setShowLocationByType(false);
      } else {
        setShowLocationByType(true);
      }
    }

    selectTab("map");
    if (locationState && locationState[0]) {
      setLocationId(locationState[0].id);
      setLocationName(locationState[0].name);
      BusinessHierarchyService.getLicenseType(locationState[0].id)
        .then((response) => {
          const licenseObj = response.data;
          setLoading(false);

          const findLicenseType = (type) => {
            const licenses = serverAvailableLicenses.current;
            const typeMatch = licenses.find((license) => license.type === type);
            if (typeMatch) {
              return { displayName: typeMatch.displayName, color: typeMatch.color.solid };
            }
            const variationMatch = licenses.find((license) => license.variations.includes(type));
            if (variationMatch) {
              return { displayName: variationMatch.displayName, color: variationMatch.color.solid };
            }
            return { displayName: type, color: "#6977F0" };
          };

          if (licenseObj) {
            setIsLicenseFetched(true);

            let result;
            if (licenseObj.currentLocationType) {
              result = findLicenseType(licenseObj.currentLocationType);
            } else if (licenseObj.parentLocationType) {
              result = findLicenseType(licenseObj.parentLocationType);
            } else if (licenseObj.customerLicenseType) {
              result = findLicenseType(licenseObj.customerLicenseType);
            }
            // else {
            //   result = findLicenseType(customerLicenseType.current);
            // }

            if (result) {
              const { displayName, color } = result;
              setLicenseType(displayName);
              licenseLevel.current = displayName;
              setLicenseColor(color);
            }

            setLocationObjLoaded(true);
          } else {
            // const result = findLicenseType(customerLicenseType.current);
            // const { displayName, color } = result;
            // setLicenseType(displayName);
            // licenseLevel.current = displayName;
            // setLicenseColor(color);
            setIsLicenseFetched(true);
            setLocationObjLoaded(true);
          }
        })
        .catch(() => {
          setLoading(false);
          setIsLicenseFetched(false);
          console.log("BusinessHierarchyService: Error on getLicenseType");
        });
    }
    prepareLocationMapDetails();
    // getRecentSearchItems();
    getUncategorisedApCounts();
  };

  const getLicenseType = () => {
    BusinessHierarchyService.getLicenseType(locationState[0].id)
      .then((response) => {
        const licenseObj = response.data;
        setLoading(false);

        const findLicenseType = (type) => {
          const licenses = serverAvailableLicenses.current;
          const typeMatch = licenses.find((license) => license.type === type);
          if (typeMatch) {
            return { displayName: typeMatch.displayName, color: typeMatch.color.solid };
          }
          const variationMatch = licenses.find((license) => license.variations.includes(type));
          if (variationMatch) {
            return { displayName: variationMatch.displayName, color: variationMatch.color.solid };
          }
          return { displayName: type, color: "#6977F0" };
        };

        if (licenseObj) {
          setIsLicenseFetched(true);

          let result;
          if (licenseObj.currentLocationType) {
            result = findLicenseType(licenseObj.currentLocationType);
          } else if (licenseObj.parentLocationType) {
            result = findLicenseType(licenseObj.parentLocationType);
          } else if (licenseObj.customerLicenseType) {
            result = findLicenseType(licenseObj.customerLicenseType);
          }
          // else {
          //   result = findLicenseType(customerLicenseType.current);
          // }

          if (result) {
            const { displayName, color } = result;
            setLicenseType(displayName);
            licenseLevel.current = displayName;
            setLicenseColor(color); // Set the color
          }

          setLocationObjLoaded(true);
        } else {
          // const result = findLicenseType(customerLicenseType.current);
          // const { displayName, color } = result;
          // setLicenseType(displayName);
          // licenseLevel.current = displayName;
          // setLicenseColor(color); // Set the color
          setIsLicenseFetched(true);
          setLocationObjLoaded(true);
        }
        setIsLicenseModalOpen(false);
      })
      .catch(() => {
        setLoading(false);
        setIsLicenseFetched(false);
        setIsLicenseModalOpen(false);
        console.log("BusinessHierarchyService: Error on getLicenseType");
      });
  };

  const onLicenseModalClose = () => {
    setIsLicenseModalOpen(false);
  };

  const onLicenseSaveSuccess = () => {
    getLicenseType();
  };

  useEffect(() => {
    if (!userDetails) return;
    const lType = userDetails.userCurrentStatus?.data?.licenseDetails?.licenseType ?? "";
    isSmartLicenseEnabled.current = userDetails.userCurrentStatus?.data?.isSmartLicenseRegistered ?? false;

    BusinessHierarchyService.getLicensesList()
      .then((data) => {
        setAvailableLicenses(data.data);
        serverAvailableLicenses.current = data.data;
        updateLicenseType(lType);
        setLicensesLoaded(true);
      })
      .catch(() => {
        console.log("BusinessHierarchyService: Error on getLicensesList");
        setLicensesLoaded(true);
      });
  }, [userDetails]);

  useEffect(() => {
    if (!licensesLoaded) return;

    if (locationUpdate) {
      if (deltaUpdateTime < locationUpdate.lastUpdated) {
        setDeltaUpdateTime(locationUpdate.lastUpdated);
        setRefreshMap(false);
        getIndexedDbGeolocationData();
        setTimeout(() => {
          setRefreshMap(true);
        }, 100);
      }
    }
  }, [locationUpdate, licensesLoaded]);

  useEffect(() => {
    if (!licensesLoaded) return;

    if (locationState) setLoading(false);
    if (locationState && locationState[0] && locationState[0].id) {
      const loc = { data: locationState[0] };
      if (selectedNodeState?.data?.id === loc?.data?.id) {
        return;
      }
      setSelectedNodeState(loc);
      selectedNode = loc;
      activeNode(loc);
      setRefreshMap(false);
      setTimeout(() => {
        setRefreshMap(true);
      }, 100);
      if (!document.querySelector(".mds-rebuild-nav")?.classList?.contains("mds-rebuild-nav-is-collapsed")) {
        document.querySelector(".mds-rebuild-nav-collapse")?.click();
      }
    }
    i18n.setDefaultNamespace("spaces-dash-app-business-hierarchy");
  }, [locationState, licensesLoaded]);

  useEffect(() => {
    if (!licensesLoaded) return;

    if (locationMap && Object.keys(locationMap).length) {
      setLocationRowMap(locationMap);
    }
    i18n.setDefaultNamespace("spaces-dash-app-business-hierarchy");
  }, [locationMap, licensesLoaded]);

  useEffect(() => {
    if (!licensesLoaded) return;

    console.log("HierarchyBase Mount");
    console.log(`${locationId} : ${locationName}`);
    setLoading(true);
    getIndexedDbGeolocationData();
    if (!rmsDependenciesLoaded) {
      if (document.getElementById("rms-script")) {
        // rms script already exists
      } else {
        prepareRMSDependencies();
      }
    }
  }, [licensesLoaded]);

  return (
    <>
      {/* <LocationLib></LocationLib> */}
      <Flex
        direction="vertical"
        gap={16}
        style={{
          "padding-top": "24px",
          "padding-bottom": "24px",
          "padding-right": "36px",
          "padding-left": "24px",
          height: "calc(100vh - 80px)",
          overflow: "hidden"
        }}
      >
        <Flex direction="vertical" gap={16}>
          <Heading size="primary" id="location-hierarchy-title">
            {i18n.t("business-location-details.location-hierarchy")}
          </Heading>
        </Flex>
        <Flex direction="vertical">
          <Container style={{ padding: "0px 0px 0px 0px" }}>
            <div className="bh-base">
              <div className={`bh-split ${locCollapsed ? "collapsed" : ""}`}>
                <span
                  className={`loc-collapse ${locCollapsed ? "collapsedIcon" : ""}`}
                  onClick={toggleCollapseLoc}
                  role="button"
                  tabIndex={-1}
                  style={{
                    "z-index": isLicenseModalOpen || modalOpened ? "0" : "900"
                  }}
                  onKeyDown={toggleCollapseLoc}
                  title="Toggle Location Hierarchy"
                >
                  {/* <img src={collapseLHIcon} alt="collapse" /> */}
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      height: 24,
                      width: 24,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      padding: "4px",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                      border: "1px solid #d9d9d9"
                    }}
                  >
                    {locCollapsed ? <CaretRight /> : <CaretLeft />}
                  </Flex>
                </span>
                <div className={`bh-loc ${searchActive ? "fixedHeight" : ""}`} style={{ paddingTop: "2px" }}>
                  <LocationTree params={treeParams} />
                </div>
                <div
                  className={`bh-main ${selectedNodeState && selectedNodeState.data && selectedNodeState.data.access !== "Full" ? "grey-overlay" : ""}`}
                  style={{
                    "border-left": locCollapsed ? "0px" : "1px solid #d9d9d9"
                  }}
                >
                  <div style={{ height: "calc(100vh - 155px)", overflow: "auto" }}>
                    {selectedNodeState && selectedNodeState.data && selectedNodeState.data.access !== "Full" && <div className="permission-overlay" />}
                    <div className="loc-container">
                      <div className="location-data">
                        <div className="loc-split">
                          <div className="left-split">
                            {selectedNodeDetails ? (
                              <BusinessLocationDetails
                                goToLocationTab={selectLocationTab}
                                selectedNode={selectedNodeState}
                                selectedNodeDetails={selectedNodeDetails}
                                wlcLocationData={wlcLocationData}
                                locationRowMap={locationRowMap}
                                setModalOpened={setModalOpened}
                                isSmartLicenseRegistered={isSmartLicenseRegistered}
                                showLocationByType={showLocationByType}
                                isLicenseFetched={isLicenseFetched}
                              />
                            ) : (
                              <ContainerLoader />
                            )}
                          </div>
                          {selectedNodeDetails && locationObjLoaded && showLocationByType && licenseType && (
                            <div className="right-split">
                              <div className="license-border">
                                <div className="license-level-label">
                                  <Text size="p3" weight="regular" color="light">
                                    {i18n.t("hierarchy-base.license-level-text")}
                                  </Text>
                                </div>
                                <div className="divTable">
                                  <div className="divTableRow">
                                    <div className="divTableCell width150">
                                      <div className="divTable150">
                                        <MTag.Colored
                                          style={{
                                            backgroundColor: licenseColor,
                                            color: "#fff"
                                          }}
                                        >
                                          {licenseType?.toUpperCase()}
                                        </MTag.Colored>
                                      </div>
                                      {/* <div className="divTable150">
                                        {licenseType == "SEE" && (
                                          <>
                                            <img alt="See" className="divCell-img" src={seeLicense} />
                                            <span className="see-label divCell-label">{i18n.t("hierarchy-base.see-text")}</span>
                                          </>
                                        )}
                                      </div>
                                      <div className="divTable150">
                                        {licenseType == "ACT" && (
                                          <>
                                            <img alt="Act" className="divCell-img" src={actLicense} />
                                            <span className="act-label divCell-label">{i18n.t("hierarchy-base.act-text")}</span>
                                          </>
                                        )}
                                      </div>
                                      <div className="divTable150">
                                        {licenseType == "EXT" && (
                                          <>
                                            <img alt="Extend" className="divCell-img" src={extendLicense} />
                                            <span className="extend-label divCell-label">{i18n.t("hierarchy-base.extend-text")}</span>
                                          </>
                                        )}
                                      </div>
                                      <div className="divTable150">{licenseType == "SMART_VENUES" && <span className="smartvenue-label divCell-label">SMART VENUES</span>}</div>
                                      <div className="divTable150">{licenseType == "SMART_OPERATIONS" && <span className="smartops-label divCell-label"> SMART OPERATIONS</span>}</div>
                                      <div className="divTable150">{licenseType == "SMART_OPERATIONS_BASE" && <span className="smartops-label divCell-label"> SMART OPERATIONS BASE</span>}</div>
                                      <div className="divTable150">{licenseType == "UNLIMITED" && <span className="unlimited-label divCell-label"> UNLIMITED</span>}</div> */}
                                    </div>
                                    {isSmartLicenseEnabled.current && locationState && locationState[0]?.id && (
                                      <div className="divTableCell">
                                        <div className="change-label">
                                          {isLicenseModalOpen && (
                                            <SelectLicense
                                              isOpen={isLicenseModalOpen}
                                              locationId={locationState[0]?.id}
                                              selectedLocation={selectedNodeState}
                                              i18n={i18n}
                                              onClose={onLicenseModalClose}
                                              onLicenseSaveSuccess={onLicenseSaveSuccess}
                                            />
                                          )}

                                          <MLink
                                            size="lg"
                                            style={{
                                              fontSize: 14
                                            }}
                                            onClick={() => setIsLicenseModalOpen(true)}
                                          >
                                            {i18n.t("hierarchy-base.change-text")}
                                          </MLink>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {changeLicensePopup && <h6>split license</h6>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bh-tabs">
                      <div className="tab-list">
                        <Flex direction="vertical" gap={24}>
                          <Flex direction="vertical">
                            <MTabs kind="secondary" style={{ "padding-left": "24px" }}>
                              <MTabs.Link
                                // as={Link}
                                id="map-heirarchy"
                                onClick={() => selectTab("map")}
                                selected={selectedTab === "map"}
                              >
                                {i18n.t("hierarchy-base.map-text")}
                              </MTabs.Link>
                              <MTabs.Link
                                // as={Link}
                                id="locInfo-heirarchy"
                                onClick={() => selectTab("locInfo")}
                                selected={selectedTab === "locInfo"}
                              >
                                {i18n.t("hierarchy-base.location-info-text")}
                              </MTabs.Link>
                              <MTabs.Link
                                // as={Link}
                                id="devices-heirarchy"
                                onClick={() => selectTab("devices")}
                                selected={selectedTab === "devices"}
                              >
                                {i18n.t("hierarchy-base.network-devices-text")}
                              </MTabs.Link>
                              <MTabs.Link
                                // as={Link}
                                id="metadata-heirarchy"
                                onClick={() => selectTab("metadata")}
                                selected={selectedTab === "metadata"}
                              >
                                {i18n.t("hierarchy-base.metadata-text")}
                              </MTabs.Link>
                              {(licenseType == "ACT" || licenseType == "UNLIMITED" || licenseType == "SMART_VENUES") && (
                                <MTabs.Link id="rules-heirarchy" onClick={() => selectTab("rules")} selected={selectedTab === "rules"}>
                                  {i18n.t("hierarchy-base.rules-text")}
                                </MTabs.Link>
                              )}
                              <MTabs.Link id="nodes-heirarchy" onClick={() => selectTab("nodes")} selected={selectedTab === "nodes"}>
                                {i18n.t("hierarchy-nodes.locations-text")}
                              </MTabs.Link>
                              <MTabs.Link id="users-heirarchy" onClick={() => selectTab("users")} selected={selectedTab === "users"}>
                                {i18n.t("hierarchy-base.users-text")}
                              </MTabs.Link>
                              {locationObjLoaded && showLocationByType && isSmartLicenseEnabled.current && (
                                <MTabs.Link
                                  // as={Link}
                                  id="license-heirarchy"
                                  onClick={() => selectTab("license")}
                                  selected={selectedTab === "license"}
                                >
                                  {i18n.t("hierarchy-base.license-usage-text")}
                                </MTabs.Link>
                              )}
                            </MTabs>
                          </Flex>
                        </Flex>
                      </div>
                      {selectedTab == "map" && (
                        <div className="tab-content">
                          {/* <h6>Map Content</h6> */}
                          {canShow3DSwitch() && (
                            <div className={`switch-3d-container ${zoneDrawerOpen ? "zoneDrawerOpen" : ""}`}>
                              {richMap && show3DMap && (
                                <div
                                  title="Toggle between 3D map and 2D map"
                                  onClick={() => toggle3DView(false)}
                                  role="button"
                                  onKeyDown={() => {
                                    toggle3DView(false);
                                  }}
                                  tabIndex={-5}
                                >
                                  <img src={_3D} alt="3d-2d" />
                                </div>
                              )}
                              {richMap && !show3DMap && (
                                <div
                                  title="Toggle between 3D map and 2D map"
                                  onClick={() => toggle3DView(true)}
                                  role="button"
                                  onKeyDown={() => {
                                    toggle3DView(true);
                                  }}
                                  tabIndex={-4}
                                >
                                  <img src={_2D} alt="3d-2d" />
                                </div>
                              )}
                              {!richMap && (
                                <div
                                  className="disabled-3d-switch"
                                  title="Rich Maps not enabled"
                                  onClick={() => toggle3DView(true)}
                                  role="button"
                                  onKeyDown={() => {
                                    toggle3DView(true);
                                  }}
                                  tabIndex={-3}
                                >
                                  <img src={mapDisabled} alt="3d-2d" />
                                </div>
                              )}
                            </div>
                          )}
                          {richMap && show3DMap && (
                            <div
                              className="rms-tooltip"
                              onClick={() => toggleRMSHint()}
                              role="button"
                              onKeyDown={() => {
                                toggleRMSHint();
                              }}
                              tabIndex={-2}
                            >
                              <div className="rms-info-btn">
                                <img src={rmsInfo} alt="Info icon" />
                              </div>
                            </div>
                          )}{" "}
                          {displayHint && (
                            <div className="map-overlay">
                              <div className="map-hint">
                                <div className="map-hint-heading">
                                  <div>{i18n.t("hierarchy-base.use-map-button-text")}</div>
                                  <div>
                                    {i18n.t("hierarchy-base.alternatively-press-text")}
                                    <span>{i18n.t("hierarchy-base.control-key-right-button-text")}</span>
                                    {i18n.t("hierarchy-base.drag-to-adjust-map-text")}
                                  </div>
                                </div>
                                <div className="map-hint-labels">
                                  <div className="label1"> {i18n.t("hierarchy-base.track-pad-controls-text")}</div>
                                  <div className="label2">{i18n.t("hierarchy-base.mouse-controls-text")}</div>
                                </div>
                                <div className="map-hint-controls map-hint1">
                                  <div className="ctrl">
                                    {i18n.t("hierarchy-base.control-plus-text")}
                                    <span>
                                      <img src={zoomTouch} alt="touch control" />
                                    </span>
                                  </div>
                                  <div className="ctrl">{i18n.t("hierarchy-base.adjust-pitch-text")}</div>
                                  <div className="ctrl">
                                    {i18n.t("hierarchy-base.control-plus-text")}
                                    <span>
                                      <img src={moveMouse} alt="mouse control" />
                                    </span>
                                  </div>
                                </div>
                                <div className="map-hint-controls">
                                  <div className="ctrl">
                                    {i18n.t("hierarchy-base.control-plus-text")}
                                    <span>
                                      <img src={zoomTouchAlt} alt="touch control" />
                                    </span>
                                  </div>
                                  <div className="ctrl">{i18n.t("hierarchy-base.adjust-pitch-text")}</div>
                                  <div className="ctrl">
                                    {i18n.t("hierarchy-base.control-plus-text")}
                                    <span>
                                      <img src={moveMouseAlt} alt="mouse control" />
                                    </span>
                                  </div>
                                </div>
                                <div className="hint-button">
                                  <span
                                    onClick={() => hideHint()}
                                    role="button"
                                    onKeyDown={() => {
                                      hideHint();
                                    }}
                                    tabIndex={-1}
                                  >
                                    {i18n.t("hierarchy-base.got-it-text")}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {richMapCheckingLoader && (
                            <div className="richmaploader-container">
                              <div className="richmap-loader">
                                <img src={mapLoader} alt="map loading..." />
                              </div>
                            </div>
                          )}
                          {selectedNodeState && !isFloorLocation && refreshMap && googleMapApiKey && (
                            <div className="map-tab-container">
                              {/* <h6>Location Map Component</h6> */}
                              <div id="google-map">
                                <LocationMap node={selectedNodeState} geolocations={geoLocationData} googleMapApiKey={googleMapApiKey} />
                              </div>
                            </div>
                          )}
                          {selectedNodeState && isFloorLocation && mapboxApiKey && (
                            <div>
                              {showRms ? (
                                <div className="richmap-tab-container">
                                  {/* <h6>Rich Map Component</h6> */}
                                  <RmsBase node={rmsFloor} buildingData={rmsBuilding} />
                                </div>
                              ) : (
                                <div className={`floormap-tab-container ${zoneDrawerOpen ? "zoneDrawerOpen" : ""}`}>
                                  {/* <h6>2D Floor Map Component</h6> */}
                                  <FloorMap setZoneDrawerOpen={setZoneDrawerOpen} nodeData={selectedNodeState} locationRowMap={locationRowMap} i18n={i18n} mapboxApiKey={mapboxApiKey} />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {selectedTab == "locInfo" && (
                        <div className="loc-info-content">
                          <LocationInfo
                            node={selectedNodeState}
                            selectedNodeDetails={selectedNodeDetails}
                            setModalOpened={setModalOpened}
                            selectedNodeLocationCounts={selectedNodeLocationCounts}
                            googleMapApiKey={googleMapApiKey}
                          />
                        </div>
                      )}
                      {selectedTab == "devices" && (
                        <div className="tab-content">
                          <NetworkDevicesBase selectedNode={selectedNodeState} locationRowMap={locationRowMap} />
                        </div>
                      )}
                      {selectedTab == "metadata" && (
                        <div>
                          <LocationMetadata setModalOpened={setModalOpened} node={selectedNodeDetails} />
                        </div>
                      )}
                      {selectedTab == "license" && isSmartLicenseEnabled.current && (
                        <div className="tab-content">
                          <div className="license-tab-content">
                            <LicenseUsage
                              locationId={selectedNodeState?.data?.id}
                              locationName={selectedNodeState?.data?.name}
                              isSmartLicenseEnabled={isSmartLicenseEnabled.current}
                              availableLicenses={availableLicenses}
                            />
                          </div>
                        </div>
                      )}
                      {selectedTab == "rules" && (
                        <div>
                          <ProximityRules selectedNodeValue={selectedNodeState} />
                        </div>
                      )}
                      {selectedTab == "nodes" && (
                        <div>
                          <HierarchyNodes selectedNodeValue={selectedNodeState} setModalOpened={setModalOpened} />
                        </div>
                      )}
                      {selectedTab == "users" && (
                        <div>
                          <div className="user-flex">
                            <div className="usersearch-container">
                              <Search
                                onChange={(e) => {
                                  searchByAdminRoles(e, pathname);
                                }}
                                onClear={onClearSearch}
                                options={[]}
                                placeholder="Search"
                                value={searchBy}
                                label=""
                              />
                            </div>
                          </div>
                          <MemoizedUserTable searchBy={searchBy} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </Flex>
      </Flex>
    </>
  );
}

export default HierarchyBase;
